import { capitalize } from 'underscore.string';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { PostList } from 'src/modules/community/communityPosts/components/postList';
import i18n from 'src/utils/translate';
import { useSelector } from 'src/store/store';
import postsFilterOptions from 'src/constants/postsFilterOptions';
import { months } from 'src/utils/date';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import CommunityFooterCTA from 'src/modules/community/shared/components/communityFooterCTA';
import PostFilterInline from 'src/components/postFilterInline';
import Pagination from 'src/components/pagination/pagination';
import GlobalSearch from 'src/modules/search/shared/components/searchBar';
import { useBranch } from 'src/utils/cookie';
import { titleMedium, titleSmall } from 'styles-js/mixins/typography';
import { useMediaQuery } from 'src/utils/mediaQuery';
export default function CommunityPostsContent() {
  const router = useRouter();
  const currentCommunity = useSelector(({
    context
  }) => context.currentCommunity);
  const {
    archive = {},
    filter = 'latest',
    posts = [],
    selectedTopic = '',
    totalMonthlyPosts = 0
  } = useSelector(({
    pages
  }) => pages.communityPosts);
  const isA = useBranch() !== 'B';
  const isMobile = useMediaQuery('underScreenSmall');
  const redirectToPostsTopicPage = ({
    communitySlug,
    topicName,
    filter
  }: {
    communitySlug?: string;
    topicName: string;
    filter?: string;
  }): void => {
    const filterHash = filter ? `#${filter}` : '';
    router.push(`/community/communityPosts?communitySlug=${communitySlug}&topicName=${topicName}`, `/${communitySlug}/posts/topic/${topicName}${filterHash}`, {
      shallow: true
    });
  };
  const redirectToPollsPage = (communitySlug?: string): void => {
    router.push(`/community/communityPosts?communitySlug=${communitySlug}`, `/${communitySlug}/polls`, {
      shallow: true
    });
  };
  const redirectToPostsPage = ({
    communitySlug,
    filter
  }: {
    communitySlug?: string;
    filter?: string;
  }): void => {
    const filterHash = filter ? `#${filter}` : '';
    router.push(`/community/communityPosts?communitySlug=${communitySlug}`, `/${communitySlug}/posts${filterHash}`, {
      shallow: true
    });
  };
  const handleFilterClick = (newFilter: string) => {
    if (selectedTopic) {
      redirectToPostsTopicPage({
        communitySlug: currentCommunity?.slug,
        topicName: encodeURIComponent(selectedTopic.toLowerCase()),
        filter: newFilter
      });
    } else if (newFilter === 'polls') {
      redirectToPollsPage(currentCommunity?.slug);
    } else {
      redirectToPostsPage({
        communitySlug: currentCommunity?.slug,
        filter: newFilter
      });
    }
    trackFootfallEvent(Event.Clicked, {
      clickType: 'community-posts-filter',
      clickMetadata: {
        section: Section.CommunityPosts,
        element: newFilter
      }
    });
  };
  const currentMonth = months.find(momentMonth => momentMonth.twoDigits === archive.currentMonth);
  return <Wrapper data-sentry-element="Wrapper" data-sentry-component="CommunityPostsContent" data-sentry-source-file="communityPostsContent.tsx">
      {posts?.length > 0 && isMobile && isA && <StyledSearchBar isCommunitySearch={true} isSmall={true} placeholder={i18n.t('Search posts on ') + currentCommunity?.name} />}
      {selectedTopic && <H3 data-testid="community-posts-heading">{capitalize(selectedTopic)}{' '}{i18n.t('posts')}</H3>}

      {archive.currentYear ? <H3 data-testid="community-posts-heading">{i18n.t('All posts for')} {currentMonth && i18n.t(currentMonth.name)} {archive.currentYear}</H3> : <Filters data-testid="community-posts-filters">
            <p>{i18n.t('Sort posts by')}:</p>
            <PostFilterInline options={postsFilterOptions} selected={filter} onFilterClick={handleFilterClick} />
          </Filters>}

      <PostList data-sentry-element="PostList" data-sentry-source-file="communityPostsContent.tsx" />

      {!archive.currentYear && <CommunityFooterCTA />}

      {archive.currentYear && totalMonthlyPosts > 0 && <div data-testid="search-pagination">
          <Pagination isShowBotLinks={true} total={totalMonthlyPosts} />
        </div>}
    </Wrapper>;
}
const Wrapper = styled.div`
  @media (${({
  theme
}) => theme.underScreenSmall}) {
    margin-bottom: 30px;
  }
`;
const H3 = styled.h3`
  ${titleMedium}
  margin-bottom: 30px;
  border-bottom: ${({
  theme
}) => theme.greyLine};
  padding-bottom: 5px;
  
  @media (${({
  theme
}) => theme.underScreenSmall}) {
    margin: 0 15px 30px;
  }
`;
const Filters = styled.div`
  margin-bottom: 25px;

  @media (${({
  theme
}) => theme.underScreenSmall}) {
    margin: 0 15px 25px;
  }

  p {
    ${titleSmall}
    margin-bottom: 8px;
  }
`;
const StyledSearchBar = styled(GlobalSearch)`
  padding: 0 15px;
  margin-bottom: 20px;
`;